import { inject, Injectable, isDevMode } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';
import { OidcEncryptedStorageService } from '../../../../../../shared/src/lib/feature-seatmap/services/oidc-encryption/oidc-encrypted-storage.service';

/**
 * Custom storage implementation for angular-auth-oidc-client.
 * Uses AES-256-GCM encryption via OidcEncryptedStorageService to protect
 * all OIDC data (tokens, user info, well-known endpoints) at rest.
 *
 * The service delegates to OidcEncryptedStorageService which treats OIDC data
 * as a "virtual user" in the unified encrypted storage system.
 */
@Injectable()
export class OcpStorage extends AbstractSecurityStorage {
  private readonly encryptedStorage = inject(OidcEncryptedStorageService);

  read(key: string): string | null {
    if (!this.encryptedStorage.isReady()) {
      if (isDevMode()) {
        console.warn(`[OcpStorage] read('${key}') called before encryption initialized`);
      }
      return null;
    }

    const value = this.encryptedStorage.read(key);
    if (value === null || value === undefined) {
      return null;
    }

    // The OIDC library expects a JSON string that it will JSON.parse().
    // Always stringify the stored value to ensure valid JSON output.
    return JSON.stringify(value);
  }

  write(key: string, value: unknown): void {
    if (!this.encryptedStorage.isReady()) {
      if (isDevMode()) {
        console.warn(`[OcpStorage] write('${key}') called before encryption initialized`);
      }
      return;
    }

    if (isDevMode()) {
      console.log(`[OcpStorage] Storing key: ${key}`);
    }

    // The OIDC library JSON.stringify's values before calling write(),
    // so we parse them to store as native types. read() will re-stringify.
    // This round-trip ensures: JSON string → native value → JSON string
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        this.encryptedStorage.write(key, parsed);
        return;
      } catch {
        // If parsing fails, store as-is (shouldn't happen with OIDC lib)
      }
    }

    this.encryptedStorage.write(key, value);
  }

  remove(key: string): void {
    this.encryptedStorage.remove(key);
  }

  clear(): void {
    this.encryptedStorage.clear();
  }
}
